import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import RadioGroup from "../../shared/ui/radio-group/RadioGroup";
import FormInput from "./form-input/FormInput";
import styles from "./QueryForm.module.css";
import { DatePicker } from "@mui/x-date-pickers";
import * as esigActions from "../../../store/actions/esignature";
import { Table } from "react-bootstrap";
import ResultsGrid from "../results-grid/ResultsGrid";
import * as appActions from "../../../store/actions/app";
import {
  nostatusesValidation,
  noagenciesValidation,
  baseTitle,
} from "../../../constants/messages";
import { formatDateSQL } from "../../../utilities/formatUtilities";
import NoData from "../../shared/ui/no-data/NoData";
import {
  addOrSubtractFromDate,
  dateToMoment,
  getDifferenceBetweenDates,
} from "../../../utilities/dateUtilities";
import WindowedSelect from "./windowed-select/WindowedSelect";
import { univMinDate } from "../../../utilities/validationUtilities";

function QueryForm() {
  const dispatch = useDispatch();

  const numberOfRecordsOptions = [
    { text: "25", value: 25 },
    { text: "50", value: 50 },
    { text: "All", value: 10000 },
  ];
  const today = new Date();
  let monthAgo = addOrSubtractFromDate(today, -30);
  const allAgenciesOption = { value: "All", label: "Select All" };
  const timespan = 89;

  let agencies = useSelector((state) => state.esignature.agencies);
  let envelopes = useSelector((state) => state.esignature.envelopes);
  let authenticatedAgent = useSelector(
    (state) => state.user.agentAuthenticated
  );
  let authenticatedEmployee = useSelector(
    (state) => state.user.employeeAuthenticated
  );
  let startingStatuses = useSelector((state) => state.esignature.statuses);
  let isLoading = useSelector((state) => state.app.isLoading);

  let [agency, setAgency] = useState("");
  let [toDate, setToDate] = useState(formatDateSQL(today));
  let [fromDate, setFromDate] = useState(formatDateSQL(monthAgo));
  let [statuses, setStatuses] = useState([]);
  let [numberOfRecords, setNumberOfRecords] = useState(
    numberOfRecordsOptions[2]
  );
  let [dropdownAgencies, setDropdownAgencies] = useState([
    { value: "", label: "Loading..." },
  ]); //to display loading until they load
  let [allSelected, setAllSelected] = useState(false);

  let [searched, setSearched] = useState(false);
  let [completedID, setCompletedId] = useState("6"); //6 is the expected ID of completed in the database
  let [maxToDate, setMaxToDate] = useState(toDate);

  const handleStatuses = (index) => {
    statuses[index].selected = !statuses[index].selected;
    setStatuses([...statuses]);
  };

  const handleAllSelected = () => {
    const newStatuses = [...statuses];
    newStatuses.forEach((status) => (status.selected = !allSelected));
    setStatuses(newStatuses);
    setAllSelected(!allSelected);
  };

  const handleSubmit = () => {
    let filteredStatuses = statuses
      .filter((status) => status.selected)
      .map((status) => status.Id);
    let completedStatus = false;
    if (filteredStatuses.includes(completedID)) {
      filteredStatuses = filteredStatuses.filter(
        (status) => status !== completedID
      );
      completedStatus = true;
    }
    const queryForm = {
      AgencyCode: agency?.value,
      FromDate: `${formatDateSQL(fromDate)} 01:00:00.000`,
      ToDate: `${formatDateSQL(toDate)} 23:59:59.999`,
      Statuses: filteredStatuses,
      NumberOfRecords: numberOfRecords.value,
      Completed: completedStatus,
    };
    if (filteredStatuses.length <= 0 && !queryForm.Completed) {
      dispatch(appActions.setAlertModal(true, "error", nostatusesValidation));
      setTimeout(() => {
        dispatch(appActions.setAlertModal(false, ""));
      }, 5000);
    } else if (!queryForm.AgencyCode || queryForm.AgencyCode?.length === 0) {
      dispatch(appActions.setAlertModal(true, "error", noagenciesValidation));
      setTimeout(() => {
        dispatch(appActions.setAlertModal(false, ""));
      }, 5000);
    } else {
      dispatch(esigActions.sendQuery(queryForm));
      setSearched(true);
    }
  };

  useEffect(() => {
    if (addOrSubtractFromDate(fromDate, timespan) < today) {
      setMaxToDate(formatDateSQL(addOrSubtractFromDate(fromDate, timespan)));
    } else {
      setMaxToDate(formatDateSQL(addOrSubtractFromDate(today, 0)));
    }
    if (getDifferenceBetweenDates(toDate, fromDate) < 0) {
      setToDate(formatDateSQL(fromDate));
    }
    if (getDifferenceBetweenDates(toDate, fromDate) > timespan) {
      dispatch(
        appActions.setAlertModal(
          true,
          "info",
          `There is a maximum date range of ${timespan + 1} days`
        )
      );
      setTimeout(() => {
        dispatch(appActions.setAlertModal(false));
      }, 5000);
      setToDate(formatDateSQL(addOrSubtractFromDate(fromDate, timespan)));
    }
  }, [dispatch, fromDate, toDate]);

  useEffect(() => {
    if (agencies?.length < 1) {
      if (authenticatedEmployee) {
        dispatch(esigActions.getAgencies());
      } else if (authenticatedAgent) {
        dispatch(esigActions.setAgency());
      }
    }
  }, [dispatch, authenticatedEmployee, authenticatedAgent]);

  useEffect(() => {
    const mappedAgencies = [];
    if (authenticatedEmployee) {
      mappedAgencies.push(allAgenciesOption);
    }
    agencies.forEach((agency) => {
      mappedAgencies.push({
        value: agency.AgencyCode,
        label: `${agency.AgencyCode} (${agency.Name})`,
      });
    });
    if (authenticatedEmployee) {
      //so it should say loading for employee until all loaded
      if (mappedAgencies.length > 1) {
        setDropdownAgencies(mappedAgencies);
        setAgency(mappedAgencies[0]);
      }
    } else if (mappedAgencies.length > 0) {
      //so should show loading until loaded for agent
      setDropdownAgencies(mappedAgencies);
      setAgency(mappedAgencies[0]);
    }
  }, [agencies, authenticatedEmployee]);

  useEffect(() => {
    if (startingStatuses?.length < 1) {
      dispatch(esigActions.getStatuses());
    }
  }, [dispatch]);

  useEffect(() => {
    if (startingStatuses?.length > 0) {
      let transformedStatuses = startingStatuses.map((status) => {
        if (status.Abbrev !== "C") {
          //C is the abbreviation for completed
          return { ...status, selected: true };
        }
        setCompletedId(status.Id);
        return { ...status, selected: false };
      });
      setStatuses(transformedStatuses);
    }
  }, [startingStatuses]);

  useEffect(() => {
    if (startingStatuses?.length < 1) {
      dispatch(appActions.setLoading(true));
    }
  }, [dispatch]); //need so don't see error before statuses load

  useEffect(() => {
    document.title = baseTitle + " - ESignature Envelope Statuses";
  }, []);

  return (
    <>
      {startingStatuses?.length > 0 || isLoading ? (
        <div className={styles.container}>
          <form className={styles.form}>
            <div className={styles.wideRow}>
              <FormInput label="Agency" width="50%">
                <WindowedSelect
                  value={agency}
                  options={dropdownAgencies}
                  onChange={setAgency}
                  placeholder="Search by Agency Name Or Code"
                ></WindowedSelect>
              </FormInput>
            </div>

            <div className={styles.wideRow}>
              <FormInput label="From Date" width="40%">
                  <DatePicker
                    value={dateToMoment(fromDate)}
                    onChange={(date) => setFromDate(formatDateSQL(date))}
                    format="MMMM DD YYYY"
                    maxDate={dateToMoment(today)}
                    minDate={dateToMoment(univMinDate)}
                  />
              </FormInput>

              <FormInput label="To Date" width="40%">
                  <DatePicker
                    value={dateToMoment(toDate)}
                    onChange={(date) => setToDate(formatDateSQL(date))}
                    format="MMMM DD YYYY"
                    maxDate={dateToMoment(maxToDate)}
                    minDate={dateToMoment(fromDate)}
                  />
              </FormInput>
            </div>

            <div className={styles.tableRow}>
              <FormInput label="Envelope Status" width="100%">
                <Table striped borderless>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={handleAllSelected}
                        />
                      </th>
                      <th>Select All Envelope Statuses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statuses.map((status, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            <input
                              type="checkbox"
                              checked={status.selected}
                              onChange={() => handleStatuses(index)}
                            />
                          </td>
                          <td>
                            {<strong>{status.Name}</strong>}{" "}
                            {status.Description.substring(status.Name.length)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </FormInput>
            </div>

            <div className={styles.belowTableRow}>
              <RadioGroup
                options={numberOfRecordsOptions}
                handleSelect={setNumberOfRecords}
                selected={numberOfRecords}
                groupName="numRecords"
                label="Number of records to return"
                width="50%"
              />
            </div>

            <button
              className={styles.reportButton}
              type="button"
              onClick={() => handleSubmit()}
            >
              Search
            </button>
          </form>

          {envelopes?.length > 0 ? (
            <ResultsGrid />
          ) : (
            searched &&
            !isLoading && <NoData message="No Envelopes" height="40%" />
          )}
        </div>
      ) : (
        <NoData message="An error has occurred. Please try again later." />
      )}
    </>
  );
}

export default QueryForm;
